import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import HeroSearch from "../components/HeroSearch";
import Seo from "../components/Seo";
import { Sparkles, BarChart3, Crown, ShieldCheck, Zap, Globe } from "lucide-react";
import { endpoints } from "../lib/api";
import { useCurrency } from "../lib/currency";

const features = [
  {
    icon: BarChart3,
    title: "Side-by-side price comparison",
    body: "Register, renewal, transfer, and 3-year totals — laid out clearly so you can spot the renewal traps before you click.",
  },
  {
    icon: Sparkles,
    title: "AI Name Studio",
    body: "Our AI generates brandable names with memorability and brand scores tuned to your tone.",
  },
  {
    icon: Crown,
    title: "Premium hand-picked domains",
    body: "Curated by industry — AI, fintech, hotel tech, fashion, real estate, quick commerce, D2C.",
  },
  {
    icon: ShieldCheck,
    title: "Trust-rated registrars",
    body: "We aggregate trust scores, WHOIS privacy availability and support speed to help you decide.",
  },
];

export default function Home() {
  const [insights, setInsights] = useState(null);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    endpoints.insights().then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <Seo
        description="The honest domain price comparison engine — live registration, renewal and 3-year totals from top providers. Plus AI-powered startup name ideas and curated premium inventory."
        keywords="domain price comparison, cheap domains, cheapest domain registration, cheap domain provider, buy domain cheap, cheap domain price, cheap domain host"
        path="/"
      />
      {/* Hero */}
      <section className="relative" data-testid="hero">
        <div className="absolute inset-x-0 top-0 -z-10 h-[420px] bg-grid" />
        <div className="relative mx-auto max-w-3xl py-12 md:py-20">
          <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-700">
            <Sparkles className="h-3 w-3 text-indigo-600" />
            Live prices · AI assist · RDAP
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl">
            Find the perfect domain <br className="hidden sm:block" />
            at the best price.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-600 md:text-lg">
            The honest <strong>domain price comparison</strong> engine — live registration, renewal and 3-year totals from top providers. Plus AI-powered startup name ideas and curated premium inventory.
          </p>
          <div className="mt-8 max-w-2xl">
            <HeroSearch size="lg" />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
            <span className="font-mono">Try:</span>
            {["travelai.com", "neuralforge.ai", "ledgerflow.io"].map((d) => (
              <Link
                key={d}
                to={`/compare?name=${encodeURIComponent(d)}`}
                className="rounded-full border border-zinc-200 bg-white px-3 py-1 font-mono text-zinc-700 hover:border-indigo-200 hover:text-indigo-700"
                data-testid={`example-${d}`}
              >
                {d}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="mt-16" data-testid="features">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Toolkit</div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
              Everything you need to launch a brand
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {features.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              data-testid={`feature-${title}`}
              className="group rounded-2xl border border-zinc-100 bg-white p-7 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md soft-shadow"
            >
              <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-semibold text-zinc-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trending TLDs */}
      {insights && (
        <section className="mt-16" data-testid="trending-section">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Trending</div>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">TLD pulse this quarter</h2>
            </div>
            <Link to="/blog" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">View all →</Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {insights.trending_tlds.map((t) => (
              <div key={t.tld} className="rounded-xl border border-zinc-100 bg-white p-4 transition-all hover:border-indigo-200 hover:shadow-sm" data-testid={`tld-${t.tld}`}>
                <div className="font-mono text-2xl font-semibold text-zinc-900">{t.tld}</div>
                <div className="mt-1 flex items-center gap-1 text-xs">
                  <Zap className="h-3 w-3 text-emerald-500" />
                  <span className="font-medium text-emerald-600">+{t.growth}%</span>
                </div>
                <div className="mt-2 text-xs text-zinc-500 font-mono">avg {formatPrice(t.avg_price)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA banner */}
      <section className="mt-20 rounded-3xl bg-zinc-900 p-10 md:p-14" data-testid="cta-banner">
        <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-300">For founders</div>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
              Stop overpaying for your first domain.
            </h2>
            <p className="mt-3 max-w-xl text-zinc-300">
              The cheapest registrar isn't always the best — but you can stop guessing. Get the full price picture in 2 seconds.
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/compare"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-medium text-zinc-900 hover:bg-zinc-100"
            >
              <Globe className="h-4 w-4" /> Compare now
            </Link>
            <Link
              to="/ai-finder"
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-700 px-6 py-3 text-sm font-medium text-white hover:bg-zinc-800"
            >
              <Sparkles className="h-4 w-4" /> AI Studio
            </Link>
          </div>
        </div>
      </section>

      {/* SEO content block — keyword-rich answers for crawlers */}
      <section className="mt-20" data-testid="home-seo-block">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">FAQ & guides</div>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
          Find the cheapest domain — answered
        </h2>

        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900">What is the cheapest domain registration in 2026?</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              For .com, Spaceship leads at $7.49/year. Porkbun follows at $9.73 and Cloudflare at $9.59 at-cost. On renewals, Cloudflare wins at $9.59 forever. See the{" "}
              <Link to="/cheapest-domain-registration" className="font-medium text-indigo-600 hover:text-indigo-500">cheapest domain registration ranking</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900">How does the domain price comparison work?</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              We pull live pricing from various hosts daily, verify availability via RDAP, and surface registry-premium warnings. Read our full{" "}
              <Link to="/domain-price-comparison" className="font-medium text-indigo-600 hover:text-indigo-500">domain price comparison guide</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900">Can I buy a cheap domain with email?</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              Yes. Namecheap bundles Private Email from $7.88/year on top of a $10.69 .com. See our{" "}
              <Link to="/cheap-domain-and-email" className="font-medium text-indigo-600 hover:text-indigo-500">cheap domain + email comparison</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900">Cheap domain and hosting bundle?</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              Hostinger and Namecheap throw in a free domain with annual hosting plans. Full{" "}
              <Link to="/cheap-domain-and-hosting" className="font-medium text-indigo-600 hover:text-indigo-500">cheap domain and hosting guide</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900">Who is the best cheap domain provider?</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              Spaceship for first-year, Cloudflare for renewals, Porkbun as the best all-rounder. Full{" "}
              <Link to="/cheap-domain-provider" className="font-medium text-indigo-600 hover:text-indigo-500">best cheap domain providers ranking</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900">How can I buy a domain cheap and keep it cheap?</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600">
              Register at the cheapest first-year provider, then transfer to a low-renewal registrar after 60 days.{" "}
              <Link to="/buy-domain-cheap" className="font-medium text-indigo-600 hover:text-indigo-500">2026 buyer's guide</Link>.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
